import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { ShieldCheck, ShieldOff, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * MFA enrolment / status panel for the Director.
 * Steps: status → password gate → QR + backup codes → 6-digit verify → enabled.
 */
export default function MFAPanel() {
    const [status, setStatus] = useState(null);
    const [step, setStep] = useState("idle"); // idle | password | qr | verify | done
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [setupData, setSetupData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [copiedIdx, setCopiedIdx] = useState(null);

    const loadStatus = async () => {
        try {
            const { data } = await api.get("/auth/mfa/status");
            setStatus(data);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => { loadStatus(); }, []);

    const startEnroll = () => {
        setStep("password");
        setErr("");
        setPassword("");
    };

    const submitPassword = async () => {
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.post("/auth/mfa/setup", { password });
            setSetupData(data);
            setStep("qr");
            setPassword("");
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || "Setup failed");
        } finally {
            setBusy(false);
        }
    };

    const verifyEnroll = async () => {
        if (code.length !== 6) {
            setErr("Code must be 6 digits");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            await api.post("/auth/mfa/verify", { code });
            toast.success("MFA armed. Future logins require your authenticator.");
            setStep("done");
            setCode("");
            await loadStatus();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || "Verification failed");
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        const pw = window.prompt("Confirm password to disable MFA:");
        if (!pw) return;
        setBusy(true);
        try {
            await api.post("/auth/mfa/disable", { password: pw });
            toast.success("MFA disarmed.");
            await loadStatus();
            setStep("idle");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Disable failed");
        } finally {
            setBusy(false);
        }
    };

    const copyCode = (c, idx) => {
        navigator.clipboard.writeText(c);
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(null), 1800);
    };

    const armed = status?.mfa_enabled;

    return (
        <div className="glass rounded-2xl p-6 border border-[hsl(40_40%_25%/0.3)]" data-testid="mfa-panel">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-3">
                    {armed ? (
                        <ShieldCheck className="w-5 h-5 text-[hsl(45_85%_68%)]" />
                    ) : (
                        <ShieldOff className="w-5 h-5 text-muted-foreground" />
                    )}
                    <div>
                        <div className="font-heading text-xl tracking-tight">Multi-Factor Authentication</div>
                        <div className="text-xs text-muted-foreground mt-1">
                            {armed
                                ? `Armed · ${status?.backup_codes_remaining ?? 0} backup codes remaining`
                                : "Required for Director-class accounts"}
                        </div>
                    </div>
                </div>
                {armed ? (
                    <button
                        onClick={disable}
                        disabled={busy}
                        data-testid="mfa-disable-btn"
                        className="px-4 py-2 rounded-md text-xs uppercase tracking-[0.25em] border border-[hsl(350_60%_40%/0.4)] text-[hsl(350_80%_72%)] hover:bg-[hsl(350_60%_20%/0.2)] transition-colors disabled:opacity-50"
                    >
                        Disarm MFA
                    </button>
                ) : (
                    step === "idle" && (
                        <button
                            onClick={startEnroll}
                            data-testid="mfa-enroll-btn"
                            className="px-4 py-2 rounded-md text-xs uppercase tracking-[0.25em] bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        >
                            Arm MFA
                        </button>
                    )
                )}
            </div>

            {step === "password" && (
                <div className="space-y-3" data-testid="mfa-password-step">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm your password</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="mfa-password-input"
                        className="w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)]"
                    />
                    <button
                        onClick={submitPassword}
                        disabled={!password || busy}
                        data-testid="mfa-password-submit"
                        className="px-4 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                    >
                        {busy ? "Generating secret..." : "Generate QR"}
                    </button>
                    {err && <div className="text-sm text-[hsl(350_80%_72%)]">{err}</div>}
                </div>
            )}

            {step === "qr" && setupData && (
                <div className="space-y-5" data-testid="mfa-qr-step">
                    <div className="grid md:grid-cols-2 gap-6 items-start">
                        <div className="bg-white rounded-lg p-3 inline-block">
                            <img src={setupData.qr_code} alt="MFA QR" className="block w-48 h-48" data-testid="mfa-qr-img" />
                        </div>
                        <div className="space-y-3 text-sm">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Manual key</div>
                                <code className="font-mono text-xs bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] rounded px-2 py-1.5 block break-all">
                                    {setupData.secret}
                                </code>
                            </div>
                            <div className="text-xs text-muted-foreground leading-relaxed">
                                Scan with Google Authenticator, Authy, or 1Password.
                                Then enter the 6-digit code below to arm.
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <KeyRound className="w-4 h-4 text-[hsl(45_85%_68%)]" />
                            <div className="text-[11px] uppercase tracking-[0.25em] text-[hsl(45_85%_68%)]">Backup Codes — save these now</div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-xs" data-testid="mfa-backup-codes">
                            {setupData.backup_codes.map((c, i) => (
                                <button
                                    key={i}
                                    onClick={() => copyCode(c, i)}
                                    className="flex items-center justify-between gap-1 px-2 py-1.5 rounded bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] hover:border-[hsl(45_85%_68%)] transition-colors"
                                >
                                    <span>{c}</span>
                                    {copiedIdx === i ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 opacity-50" />}
                                </button>
                            ))}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-2">
                            Each code is one-shot. Store them in a vault — they will not be displayed again.
                        </div>
                    </div>

                    <div className="border-t border-[hsl(40_40%_25%/0.2)] pt-4 space-y-2">
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Enter 6-digit code from your authenticator</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                            data-testid="mfa-verify-code"
                            className="w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] font-mono tracking-[0.4em] text-center"
                            placeholder="000000"
                        />
                        <button
                            onClick={verifyEnroll}
                            disabled={code.length !== 6 || busy}
                            data-testid="mfa-verify-btn"
                            className="w-full px-4 py-2.5 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                        >
                            {busy ? "Arming..." : "Arm MFA"}
                        </button>
                        {err && <div className="text-sm text-[hsl(350_80%_72%)]">{err}</div>}
                    </div>
                </div>
            )}

            {step === "done" && (
                <div className="text-sm text-emerald-300 flex items-center gap-2" data-testid="mfa-done">
                    <ShieldCheck className="w-4 h-4" /> MFA armed. Test it on your next login.
                </div>
            )}
        </div>
    );
}
